// src/pages/Services.tsx
import { Link } from "react-router-dom";
import LayoutWrapper from "../components/layout/LayoutWrapper";
import Section from "../components/ui/Section";

const Services = () => {
  return (
    <div className="bg-bg">
      {/* HERO / INTRO */}
      <section className="py-14 sm:py-16 border-b border-line">
        <LayoutWrapper>
          <div className="grid lg:grid-cols-[1.4fr,1fr] gap-10 items-start">
            <div>
              <p className="kicker mb-4">Services</p>
              <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold text-ink mb-4">
                Turn a real operational need into a governed AI system.
              </h1>
              <p className="text-sm sm:text-base text-muted max-w-xl mb-6">
                NOVTIA&apos;s services are a core delivery capability, not
                an add-on to a single product. We combine business analysis,
                trust frameworks, hands-on engineering, and reusable NOVTIA
                capabilities to take you from AS-IS processes to dependable
                AI in production.
              </p>
              <div className="flex flex-wrap gap-2 mb-6">
                <span className="tag-chip">AS-IS to TO-BE process mapping</span>
                <span className="tag-chip">Governance-first AI architectures</span>
                <span className="tag-chip">
                  Production-grade LLM &amp; ML systems
                </span>
              </div>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/contact"
                  className="inline-flex items-center bg-signal px-5 py-2 text-sm font-semibold text-on-signal hover:bg-signal-strong transition-colors"
                >
                  Talk about a project
                </Link>
                <Link
                  to="/case-studies"
                  className="inline-flex items-center border border-line-strong px-5 py-2 text-sm text-ink hover:border-signal hover:text-signal transition-colors"
                >
                  View case studies
                </Link>
              </div>
            </div>

            {/* Side panel: who it's for */}
            <div className="surface-card p-5 sm:p-6 text-sm">
              <p className="kicker mb-3">Who we work with</p>
              <ul className="space-y-3">
                <li>
                  <span className="font-semibold text-ink">
                    Heads of Data / AI
                  </span>{" "}
                  <span className="text-muted">
                    who need governance and observability baked into AI
                    initiatives, not bolted on later.
                  </span>
                </li>
                <li>
                  <span className="font-semibold text-ink">
                    Product &amp; Operations leaders
                  </span>{" "}
                  <span className="text-muted">
                    looking to automate workflows without creating new risk
                    surfaces.
                  </span>
                </li>
                <li>
                  <span className="font-semibold text-ink">
                    Risk &amp; Compliance teams
                  </span>{" "}
                  <span className="text-muted">
                    who must answer &quot;what did the AI do and why?&quot;
                    with evidence, not guesses.
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </LayoutWrapper>
      </section>

      {/* SIX SPECIALISATIONS */}
      <section className="border-b border-line">
        <Section
          eyebrow="What we deliver"
          title="Five specialisations, one delivery partner."
          description="Engage one specialisation on its own, or combine them into an end-to-end programme. Each engagement applies the right mix of bespoke engineering, NOVTIA products, and reusable platform capabilities for the operating environment."
        >
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              {
                title: "AI Strategy & Process Transformation",
                bullets: [
                  "Map how work currently moves across people, data and systems before proposing automation.",
                  "Identify genuine AI opportunities using business and risk lenses, not hype.",
                  "Redesign inefficient processes, then decide where automation, ML or agents add real value.",
                  "Roadmaps aligned to regulatory constraints and internal capabilities.",
                ],
              },
              {
                title: "AI Product & System Engineering",
                bullets: [
                  "Design and build production AI systems: predictive models, document intelligence, RAG assistants, knowledge-graph reasoning.",
                  "Integrate with your existing platforms, APIs, data warehouses and internal tools.",
                  "MLOps / LLMOps pipelines for deployment, monitoring and iteration.",
                  "Hand-over, training and joint squads with your internal engineers.",
                ],
              },
              {
                title: "Governed Agents & Runtime Control",
                bullets: [
                  "Design multi-step, tool-using agents that pass through a policy checkpoint before every action, not after it.",
                  "Permissions, escalation paths and human approval built into the execution path itself.",
                  "Immutable audit trails for every tool call, decision and outcome.",
                  "Delivered through AIRG — our runtime control plane for agent actions — or a tailored integration for your operating environment.",
                ],
              },
              {
                title: "Private AI Deployment",
                bullets: [
                  "Regional or private deployment for workloads that can't leave your environment.",
                  "Data residency, retention and access controls designed in from the start.",
                  "Restricted external data transfer and customer-controlled infrastructure.",
                  "Self-hosted or private model options where required.",
                ],
              },
              {
                title: "AI Operations & Optimisation",
                bullets: [
                  "Ongoing monitoring of reliability, quality, cost and business outcomes.",
                  "Continuous improvement of both the AI system and the workflow around it.",
                  "Incident response and drift detection for models and agents in production.",
                  "Regular reporting for the stakeholders who need to see it's working.",
                ],
              },
            ].map((item) => (
              <div
                key={item.title}
                className="surface-card surface-card-hover p-6"
              >
                <h2 className="font-display text-base font-semibold text-ink mb-3">
                  {item.title}
                </h2>
                <ul className="space-y-2 text-sm text-muted">
                  {item.bullets.map((b) => (
                    <li key={b} className="flex gap-2.5">
                      <span className="mt-1.5 h-1 w-1 flex-none bg-signal" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Section>
      </section>

      {/* ENGAGEMENT PATTERNS */}
      <section className="border-b border-line">
        <Section
          eyebrow="How we work"
          title="From diagnostic to long-term partnership."
          description="Different organisations need different entry points. We offer a few proven starting shapes, then evolve with you."
        >
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                label: "01",
                title: "Discovery & diagnostic",
                body: "A time-boxed engagement to understand your current AI usage, constraints, and ambitions — and outline a clear, pragmatic path forward.",
                details: [
                  "Executive and technical stakeholder interviews.",
                  "Systems and data landscape mapping.",
                  "Risk, governance, and opportunity assessment.",
                  "Summary report with options and trade-offs.",
                ],
                idealFor:
                  "Leaders who know AI matters but want clarity before committing.",
              },
              {
                label: "02",
                title: "Flagship use case & PoC",
                body: "We select one high-leverage use case and design a PoC that proves value while exercising governance and observability patterns.",
                details: [
                  "End-to-end design of a single AI product or workflow.",
                  "Explicit trust requirements and evaluation strategy.",
                  "Implementation with your stack or recommended tools.",
                  "Demo, findings, and next-steps playbook.",
                ],
                idealFor:
                  "Teams needing something concrete to show execs, boards, or regulators.",
              },
              {
                label: "03",
                title: "Embedded partnership",
                body: "NOVTIA works as a long-term partner to set standards, ship products, and help you build internal capability.",
                details: [
                  "Joint squads across strategy, data, and engineering.",
                  "Governance council or review rhythms.",
                  "Shared artefacts: runbooks, playbooks, templates.",
                  "Ongoing advisory as your AI portfolio evolves.",
                ],
                idealFor:
                  "Organisations committed to AI as core infrastructure, not experiments.",
              },
            ].map((pattern) => (
              <div key={pattern.title} className="surface-card p-6">
                <p className="font-mono text-xs text-muted tracking-wide mb-3 tabular">
                  {pattern.label}
                </p>
                <h3 className="font-display text-base font-semibold text-ink mb-2">
                  {pattern.title}
                </h3>
                <p className="text-sm text-muted mb-4">{pattern.body}</p>
                <ul className="space-y-1.5 mb-4 text-xs text-muted">
                  {pattern.details.map((d) => (
                    <li key={d} className="flex gap-2">
                      <span className="mt-1 h-1 w-1 flex-none bg-line-strong" />
                      <span>{d}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-muted pt-3 border-t border-line">
                  <span className="font-semibold text-ink">Ideal for:</span>{" "}
                  {pattern.idealFor}
                </p>
              </div>
            ))}
          </div>
        </Section>
      </section>

      {/* CLOSING CTA */}
      <section>
        <Section>
          <div className="surface-card p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <p className="kicker mb-3">Where should we start?</p>
              <h2 className="font-display text-xl sm:text-2xl font-semibold text-ink mb-2">
                Bring one real workflow, system, or product to the table.
              </h2>
              <p className="text-sm text-muted max-w-xl">
                We&apos;ll explore your AS-IS reality, identify fast but safe
                moves, and sketch how NOVTIA could support — from a short
                diagnostic to long-term collaboration.
              </p>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center bg-signal px-6 py-2.5 text-sm font-semibold text-on-signal hover:bg-signal-strong transition-colors flex-none"
            >
              Start a conversation
            </Link>
          </div>
        </Section>
      </section>
    </div>
  );
};

export default Services;
